import React from 'react';
import { motion } from 'framer-motion';
import { Lock, User, ArrowRight } from 'lucide-react';
import { usePrivacyAuth } from '@/context/PrivacyAuthContext';
import VibeOnboarding from './VibeOnboarding';

const AuthGuard = ({ 
  children, 
  fallback = null, 
  showOnboarding = true,
  message = 'Please sign up to access this feature',
  allowGuest = false, 
}) => {
  const { isLoggedIn, isLoading } = usePrivacyAuth();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  // If user is logged in, show the protected content
  if (isLoggedIn) {
    return children;
  }

  // If guest access is allowed, show content
  if (allowGuest) {
    return children;
  }

  // If custom fallback is provided, use it
  if (fallback) {
    return fallback;
  }

  // If showOnboarding is true, show the onboarding component
  if (showOnboarding) {
    return <VibeOnboarding />;
  }

  // Default: Show authentication required message
  return (
    <motion.div
      className="flex items-center justify-center min-h-[400px] p-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="text-center max-w-md">
        <motion.div
          className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center border border-purple-500/30"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <Lock className="w-10 h-10 text-purple-400" />
        </motion.div>
        
        <motion.h2
          className="text-2xl font-bold text-white mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Authentication Required
        </motion.h2>
        
        <motion.p
          className="text-slate-400 mb-8 leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {message}
        </motion.p>
        
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <button
            onClick={() => window.location.reload()} // Simple way to trigger auth flow
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
          >
            <User className="w-5 h-5" />
            Get Started
            <ArrowRight className="w-5 h-5" />
          </button>
          
          <div className="text-sm text-slate-500">
            Quick setup • No email required • Privacy-first
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AuthGuard;
export { AuthGuard };